
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import TooltipLabel from "@/components/general/TooltipLabel.vue";
import BotBalanceOverTimeChart from "./BotBalanceOverTimeChart.vue";
import BotAgainstMarketChart from "./BotAgainstMarketChart.vue";
import BotTradesChart from "./BotTradesChart.vue";

// Styles
import "@/assets/css/vuetify/chips.css";
import TradingBotStats from "@/models/TradingBotStats";
import TradeModel from "@/models/Trade";

@Component({
  components: {
    TooltipLabel,
    BotBalanceOverTimeChart,
    BotAgainstMarketChart,
    BotTradesChart,
  },
})
export default class IndividualBotChartWrapper extends Vue {
  // 📃 Possible components to show in the chart wrapper
  private POSSIBLE_COMPONENTS = {
    NO_TRADES_PLACEHOLDER: 0,
    CHARTS: 1,
  };

  // 📃 Possible charts to show
  private VISIBLE_CHART = {
    BALANCE_IN_USE: 0,
    PERFORMANCE: 1,
    TRADES: 2,
  };

  // 📃 Possible time filters for the chart
  private chartFilters = {
    "1D": 1,
    "7D": 7,
    "30D": 30,
    "90D": 90,
    "Since Start": -1,
  };

  // 📦 The current bot
  @Prop({ default: undefined })
  public selectedBot?: TradingBotStats;

  // 📦 The current bot's trade history
  @Prop({ default: [] })
  public tradeHistory?: TradeModel[];

  // 📦 The chart that should be shown
  private selectedVisibleChart: number = 0;

  // 📦 The selected portfolio chart filter in days
  private selectedFilterInDays: number = this.chartFilters["7D"];

  // 📦 The bot balance over time dataset
  private botBalanceDataset: number[] = [];

  // 📦 The relative bot balance over time dataset
  private relativeBotBalanceDataset: number[] = [];

  // 📦 The token price over time dataset
  private tokenPriceDataset: number[] = [];

  // 📦 Dates for each portfolio check-up
  private dateLabels: Date[] = [];

  // 📦 The currently visible component in the chart wrapper
  private get currentlyVisibleComponent(): number {
    // if (this.selectedBot?.trade_log.length == 0) {
    //   return this.POSSIBLE_COMPONENTS.NO_TRADES_PLACEHOLDER;
    // }
    return this.POSSIBLE_COMPONENTS.CHARTS;
  }

  @Watch("selectedFilterInDays")
  private onChange(val: number, oldVal: number) {
    this.fetchBotBalanceOverTime();
  }

  mounted() {
    this.fetchBotBalanceOverTime();
  }

  /**
   * 🔨 Returns the request parameters needed when requesting the bot balance
   */
  private getRequestParams(): any {
    let requestParams: any = {
      botid: this.selectedBot!.uid,
      with_price: true,
    };

    if (this.selectedFilterInDays != -1) {
      requestParams.days = this.selectedFilterInDays;
    }
    return requestParams;
  }

  /**
   * 🚀 Fetches the selected bot's balance over time
   */
  private async fetchBotBalanceOverTime() {
    Vue.prototype.$api
      .get("/api/v1/botportfolios", this.getRequestParams())
      .then((response: any) => {
        this.dateLabels = Object.keys(response.data.result).map(
          (value: string) =>
            new Date(Number(value) * 1000 - new Date().getTimezoneOffset() * 60)
        );

        let initialValues: any = Object.values(response.data.result)[0];

        this.botBalanceDataset = Object.values(response.data.result).map(
          (payload: any) => payload.bot_portfolio
        );

        this.relativeBotBalanceDataset = Object.values(
          response.data.result
        ).map((payload: any) => {
          if (payload.bot_portfolio - initialValues.bot_portfolio == 0) {
            return 0;
          }
          return (
            ((payload.bot_portfolio - initialValues.bot_portfolio) /
              initialValues.bot_portfolio) *
            100
          );
        });

        let tokenPrices: number[] = Object.values(response.data.result).map(
          (payload: any) => payload.token_price
        );

        this.tokenPriceDataset = this.removeWeirdNumbers(tokenPrices).map(
          (currentDataPointPrice: number, index: number) => {
            if (currentDataPointPrice - tokenPrices[0] == 0) {
              return 0;
            }
            return (
              ((currentDataPointPrice - tokenPrices[0]) / tokenPrices[0]) * 100
            );
          }
        );
      });
  }

  /**
   * 🔨 Removes the odd numbers that are result of a bug in fetching data from the API
   */
  private removeWeirdNumbers(tokenPrices: number[]): number[] {
    for (let i = 0; i < tokenPrices.length; i++) {
      let currentRelativeDataPoint: number =
        ((tokenPrices[i] - tokenPrices[0]) / tokenPrices[0]) * 100;

      let previousRelativeDataPoint =
        ((tokenPrices[i - 1] - tokenPrices[0]) / tokenPrices[0]) * 100;

      if (
        Math.abs(currentRelativeDataPoint) -
          Math.abs(previousRelativeDataPoint) >
          50 ||
        Math.abs(currentRelativeDataPoint) -
          Math.abs(previousRelativeDataPoint) <
          -50
      ) {
        tokenPrices[i] = tokenPrices[i - 1];
      }
    }
    return tokenPrices;
  }
}
